import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../../appointmentsPage.module.css";
import {
  IAppointmentFilters,
  ITherapist,
  ICompany,
  IFundingSource,
  ViewType,
} from "../../types/appointments.types";
import { getLegendColors } from "../../utils/fundingSourceColors";
import { setStoredCalendarState, toDateStringLocal } from "../../utils/calendarStorage";
import { useAppointmentsContext } from "../../context/AppointmentsContext";

interface IAppointmentFiltersProps {
  filters: IAppointmentFilters;
  onFiltersChange: (filters: IAppointmentFilters) => void;
  therapists: ITherapist[];
  companies: ICompany[];
  fundingSources: IFundingSource[];
}

/**
 * Appointment Filters Component
 *
 * Filter panel for appointments with view toggles.
 */
const AppointmentFilters: React.FC<IAppointmentFiltersProps> = ({
  filters,
  onFiltersChange,
  therapists,
  companies,
  fundingSources,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleFullscreen } = useAppointmentsContext();

  // Therapist dropdown state
  const [showTherapistDropdown, setShowTherapistDropdown] = useState(false);
  const [therapistSearch, setTherapistSearch] = useState("");
  const therapistDropdownRef = useRef<HTMLDivElement>(null);

  // Company dropdown state
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  // Funding Source dropdown state
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] =
    useState(false);
  const [fundingSourceSearch, setFundingSourceSearch] = useState("");
  const fundingSourceDropdownRef = useRef<HTMLDivElement>(null);

  // Determine active view from URL
  const getActiveView = (): ViewType => {
    if (location.pathname.includes("/weekly")) return "weekly";
    if (location.pathname.includes("/monthly")) return "monthly";
    return "daily";
  };

  const activeView = getActiveView();

  const handleViewChange = (view: ViewType) => {
    setStoredCalendarState({ view, date: toDateStringLocal(new Date()) });
    navigate(`/appointments/calendar/${view}`);
  };

  const handleChange = (key: keyof IAppointmentFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({});
    setTherapistSearch("");
    setCompanySearch("");
    setFundingSourceSearch("");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        therapistDropdownRef.current &&
        !therapistDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTherapistDropdown(false);
      }
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCompanyDropdown(false);
      }
      if (
        fundingSourceDropdownRef.current &&
        !fundingSourceDropdownRef.current.contains(event.target as Node)
      ) {
        setShowFundingSourceDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter therapists by search
  const filteredTherapists = therapists
    .filter((t) => t.isActive)
    .filter((t) =>
      t.name?.toLowerCase().includes(therapistSearch?.toLowerCase() ?? "") ?? false
    );

  // Get selected therapists (support both single and multiple)
  const selectedTherapistIds = filters.therapistIds || [];
  const selectedTherapists = therapists.filter((t) =>
    selectedTherapistIds.includes(t.id)
  );

  // Handle therapist selection (multi-select)
  const handleTherapistToggle = (therapistId: number) => {
    const currentIds = filters.therapistIds || [];
    const isSelected = currentIds.includes(therapistId);

    const newIds = isSelected
      ? currentIds.filter((id) => id !== therapistId)
      : [...currentIds, therapistId];

    handleChange("therapistIds", newIds.length > 0 ? newIds : undefined);
  };

  const handleRemoveTherapist = (therapistId: number) => {
    const currentIds = filters.therapistIds || [];
    const newIds = currentIds.filter((id) => id !== therapistId);
    handleChange("therapistIds", newIds.length > 0 ? newIds : undefined);
  };

  // Filter companies by search
  const filteredCompanies = companies.filter((c) =>
    c.name?.toLowerCase().includes(companySearch?.toLowerCase() ?? "") ?? false
  );

  // Get selected companies
  const selectedCompanyIds = filters.fakeCompanyIds || [];
  const selectedCompanies = companies.filter((c) =>
    selectedCompanyIds.includes(c.id)
  );
  
  // Handle company selection (multi-select)
  const handleCompanyToggle = (companyId: number) => {
    const currentIds = filters.fakeCompanyIds || [];
    const isSelected = currentIds.includes(companyId);

    const newIds = isSelected
      ? currentIds.filter((id) => id !== companyId)
      : [...currentIds, companyId];

    handleChange("fakeCompanyIds", newIds.length > 0 ? newIds : undefined);
  };

  const handleRemoveCompany = (companyId: number) => {
    const currentIds = filters.fakeCompanyIds || [];
    const newIds = currentIds.filter((id) => id !== companyId);
    handleChange("fakeCompanyIds", newIds.length > 0 ? newIds : undefined);
  };

  // Filter funding sources by search
  const filteredFundingSources = fundingSources.filter((fs) =>
    fs.name?.toLowerCase().includes(fundingSourceSearch?.toLowerCase() ?? "") ?? false
  );

  // Get selected funding sources
  const selectedFundingSourceIds = filters.fundingSourceIds || [];
  const selectedFundingSources = fundingSources.filter((fs) =>
    selectedFundingSourceIds.includes(fs.id)
  );

  // Handle funding source selection (multi-select)
  const handleFundingSourceToggle = (fundingSourceId: number) => {
    const currentIds = filters.fundingSourceIds || [];
    const isSelected = currentIds.includes(fundingSourceId);

    const newIds = isSelected
      ? currentIds.filter((id) => id !== fundingSourceId)
      : [...currentIds, fundingSourceId];

    handleChange("fundingSourceIds", newIds.length > 0 ? newIds : undefined);
  };

  const handleRemoveFundingSource = (fundingSourceId: number) => {
    const currentIds = filters.fundingSourceIds || [];
    const newIds = currentIds.filter((id) => id !== fundingSourceId);
    handleChange("fundingSourceIds", newIds.length > 0 ? newIds : undefined);
  };

  return (
    <div className={styles.filtersSection}>
      <div className={styles.filtersGrid}>
        {/* View Toggle */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            {t("APPOINTMENTS_FILTER_VIEW")}
          </label>
          <div className={styles.viewToggles}>
            <button
              className={`${styles.viewBtn} ${
                activeView === "daily" ? styles.viewBtnActive : ""
              }`}
              onClick={() => handleViewChange("daily")}
            >
              {t("APPOINTMENTS_VIEW_DAILY")}
            </button>
            <button
              className={`${styles.viewBtn} ${
                activeView === "weekly" ? styles.viewBtnActive : ""
              }`}
              onClick={() => handleViewChange("weekly")}
            >
              {t("APPOINTMENTS_VIEW_WEEKLY")}
            </button>
            <button
              className={`${styles.viewBtn} ${
                activeView === "monthly" ? styles.viewBtnActive : ""
              }`}
              onClick={() => handleViewChange("monthly")}
            >
              {t("APPOINTMENTS_VIEW_MONTHLY")}
            </button>
          </div>
        </div>

        {/* Company Filter - Custom Dropdown */}
        <div className={styles.filterGroup} ref={companyDropdownRef}>
          <label className={styles.filterLabel}>
            {t("APPOINTMENTS_FILTER_COMPANY")}
          </label>
          <div className={styles.customSelect}>
            <div
              className={`${styles.customSelectTrigger} ${
                showCompanyDropdown ? styles.customSelectActive : ""
              }`}
              onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
            >
              <span className={styles.customSelectValue}>
                {selectedCompanies.length === 0 ? (
                  <>
                    <span className={styles.companyIcon}>🏢</span>
                    {t("APPOINTMENTS_ALL_COMPANIES")}
                  </>
                ) : (
                  <span className={styles.companyCount}>
                    {selectedCompanies.length}{" "}
                    {t("APPOINTMENTS_COMPANIES_SELECTED")}
                  </span>
                )}
              </span>
              <span className={styles.customSelectArrow}>▼</span>
            </div>

            {showCompanyDropdown && (
              <div className={styles.customSelectDropdown}>
                {/* Selected Companies Tags */}
                {selectedCompanies.length > 0 && (
                  <div className={styles.selectedTags}>
                    {selectedCompanies.map((company) => (
                      <div key={company.id} className={styles.tag}>
                        <span className={styles.tagIcon}>🏢</span>
                        <span className={styles.tagText}>{company.name}</span>
                        <button
                          className={styles.tagRemove}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCompany(company.id);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Search Box */}
                <div className={styles.dropdownSearch}>
                  <input
                    type="text"
                    className={styles.dropdownSearchInput}
                    placeholder={t("APPOINTMENTS_SEARCH_COMPANY")}
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Options */}
                <div className={styles.dropdownOptions}>
                  {/* Clear All Option */}
                  {selectedCompanies.length > 0 && (
                    <div
                      className={`${styles.dropdownOption} ${styles.clearAllOption}`}
                      onClick={() => {
                        handleChange("fakeCompanyIds", undefined);
                        setCompanySearch("");
                      }}
                    >
                      <span className={styles.optionIcon}>🔄</span>
                      <span className={styles.optionText}>
                        {t("APPOINTMENTS_CLEAR_COMPANIES")}
                      </span>
                    </div>
                  )}

                  {/* Company Options */}
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies.map((company) => (
                      <label key={company.id} className={styles.dropdownOption}>
                        <input
                          type="checkbox"
                          checked={selectedCompanyIds.includes(company.id)}
                          onChange={() => handleCompanyToggle(company.id)}
                        />
                        <span className={styles.optionText}>
                          {company.name}
                        </span>
                      </label>
                    ))
                  ) : (
                    <div className={styles.dropdownNoResults}>
                      {t("APPOINTMENTS_NO_COMPANIES_FOUND")}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Therapist Filter - Custom Dropdown */}
        <div className={styles.filterGroup} ref={therapistDropdownRef}>
          <label className={styles.filterLabel}>
            {t("APPOINTMENTS_FILTER_THERAPIST")}
          </label>
          <div className={styles.customSelect}>
            <div
              className={`${styles.customSelectTrigger} ${
                showTherapistDropdown ? styles.customSelectActive : ""
              }`}
              onClick={() => setShowTherapistDropdown(!showTherapistDropdown)}
            >
              <span className={styles.customSelectValue}>
                {selectedTherapists.length === 0 ? (
                  <>
                    <span className={styles.therapistIcon}>👥</span>
                    {t("APPOINTMENTS_ALL_THERAPISTS")}
                  </>
                ) : (
                  <span className={styles.therapistCount}>
                    {selectedTherapists.length}{" "}
                    {t("APPOINTMENTS_THERAPISTS_SELECTED")}
                  </span>
                )}
              </span>
              <span className={styles.customSelectArrow}>▼</span>
            </div>

            {showTherapistDropdown && (
              <div className={styles.customSelectDropdown}>
                {/* Selected Therapists Tags */}
                {selectedTherapists.length > 0 && (
                  <div className={styles.selectedTags}>
                    {selectedTherapists.map((therapist) => (
                      <div key={therapist.id} className={styles.tag}>
                        <span className={styles.tagIcon}>👨‍⚕️</span>
                        <span className={styles.tagText}>{therapist.name}</span>
                        <button
                          className={styles.tagRemove}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTherapist(therapist.id);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Search Box */}
                <div className={styles.dropdownSearch}>
                  <input
                    type="text"
                    className={styles.dropdownSearchInput}
                    placeholder={t("APPOINTMENTS_SEARCH_THERAPIST")}
                    value={therapistSearch}
                    onChange={(e) => setTherapistSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Options */}
                <div className={styles.dropdownOptions}>
                  {/* Clear All Option */}
                  {selectedTherapists.length > 0 && (
                    <div
                      className={`${styles.dropdownOption} ${styles.clearAllOption}`}
                      onClick={() => {
                        handleChange("therapistIds", undefined);
                        setTherapistSearch("");
                      }}
                    >
                      <span className={styles.optionIcon}>🔄</span>
                      <span className={styles.optionText}>
                        {t("APPOINTMENTS_CLEAR_THERAPISTS")}
                      </span>
                    </div>
                  )}

                  {/* Therapist List */}
                  {filteredTherapists.length === 0 ? (
                    <div className={styles.dropdownEmpty}>
                      {t("APPOINTMENTS_NO_THERAPISTS_FOUND")}
                    </div>
                  ) : (
                    filteredTherapists.map((therapist) => {
                      const isSelected = selectedTherapistIds.includes(
                        therapist.id
                      );
                      return (
                        <div
                          key={therapist.id}
                          className={`${styles.dropdownOption} ${
                            isSelected ? styles.dropdownOptionSelected : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTherapistToggle(therapist.id);
                          }}
                        >
                          <span className={styles.optionIcon}>👨‍⚕️</span>
                          <span className={styles.optionText}>
                            {therapist.name}
                          </span>
                          {isSelected && (
                            <span className={styles.checkmark}>✓</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Funding Source Filter - Custom Dropdown */}
        <div className={styles.filterGroup} ref={fundingSourceDropdownRef}>
          <label className={styles.filterLabel}>
            {t("APPOINTMENTS_FILTER_FUNDING_SOURCE")}
          </label>
          <div className={styles.customSelect}>
            <div
              className={`${styles.customSelectTrigger} ${
                showFundingSourceDropdown ? styles.customSelectActive : ""
              }`}
              onClick={() =>
                setShowFundingSourceDropdown(!showFundingSourceDropdown)
              }
            >
              <span className={styles.customSelectValue}>
                {selectedFundingSources.length === 0 ? (
                  <>
                    <span className={styles.fundingSourceIcon}>💰</span>
                    {t("APPOINTMENTS_ALL_FUNDING_SOURCES")}
                  </>
                ) : (
                  <span className={styles.fundingSourceCount}>
                    {selectedFundingSources.length}{" "}
                    {t("APPOINTMENTS_FUNDING_SOURCES_SELECTED")}
                  </span>
                )}
              </span>
              <span className={styles.customSelectArrow}>▼</span>
            </div>

            {showFundingSourceDropdown && (
              <div className={styles.customSelectDropdown}>
                {/* Selected Funding Sources Tags */}
                {selectedFundingSources.length > 0 && (
                  <div className={styles.selectedTags}>
                    {selectedFundingSources.map((fs) => (
                      <div key={fs.id} className={styles.tag}>
                        <span className={styles.tagIcon}>💰</span>
                        <span className={styles.tagText}>{fs.name}</span>
                        <button
                          className={styles.tagRemove}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFundingSource(fs.id);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Search Box */}
                <div className={styles.dropdownSearch}>
                  <input
                    type="text"
                    className={styles.dropdownSearchInput}
                    placeholder={t("APPOINTMENTS_SEARCH_FUNDING_SOURCE")}
                    value={fundingSourceSearch}
                    onChange={(e) => setFundingSourceSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Options */}
                <div className={styles.dropdownOptions}>
                  {/* Clear All Option */}
                  {selectedFundingSources.length > 0 && (
                    <div
                      className={`${styles.dropdownOption} ${styles.clearAllOption}`}
                      onClick={() => {
                        handleChange("fundingSourceIds", undefined);
                        setFundingSourceSearch("");
                      }}
                    >
                      <span className={styles.optionIcon}>🔄</span>
                      <span className={styles.optionText}>
                        {t("APPOINTMENTS_CLEAR_FUNDING_SOURCES")}
                      </span>
                    </div>
                  )}

                  {/* Funding Source List */}
                  {filteredFundingSources.length === 0 ? (
                    <div className={styles.dropdownEmpty}>
                      {t("APPOINTMENTS_NO_FUNDING_SOURCES_FOUND")}
                    </div>
                  ) : (
                    filteredFundingSources.map((fs) => {
                      const isSelected = selectedFundingSourceIds.includes(
                        fs.id
                      );
                      return (
                        <div
                          key={fs.id}
                          className={`${styles.dropdownOption} ${
                            isSelected ? styles.dropdownOptionSelected : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFundingSourceToggle(fs.id);
                          }}
                        >
                          <span className={styles.optionIcon}>💰</span>
                          <span className={styles.optionText}>{fs.name}</span>
                          {isSelected && (
                            <span className={styles.checkmark}>✓</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Patient Search */}
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>
            {t("APPOINTMENTS_FILTER_PATIENT_SEARCH")}
          </label>
          <input
            type="text"
            className={styles.filterInput}
            placeholder={t("APPOINTMENTS_PATIENT_PLACEHOLDER")}
            value={filters.patientSearch || ""}
            onChange={(e) => handleChange("patientSearch", e.target.value)}
          />
        </div>

        {/* Actions - same row as filters */}
        <div className={styles.filterActions}>
          <button
            type="button"
            className={styles.fullscreenExpandBtn}
            onClick={toggleFullscreen}
            title="מסך מלא"
            aria-label="מסך מלא"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 3H5a2 2 0 0 0-2 2v3" />
              <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
              <path d="M3 16v3a2 2 0 0 0 2 2h3" />
              <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>
          </button>
          <button
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={handleClearFilters}
          >
            {t("APPOINTMENTS_CLEAR_FILTER")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentFilters;
